import React, { useState, useEffect } from 'react';
import {
  getFacebookPages,
  saveFacebookPage
} from '../../services/facebookOAuth';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Facebook, Instagram, Check, Building2, Users } from 'lucide-react';

const normalizePage = (page) => {
  const instagramFromVariants =
    page.instagram ||
    page.instagram_business_account ||
    (page.ig_name || page.ig_account
      ? {
          id: page.ig_account || page.instagram_id || page.id,
          username: page.ig_name || page.instagram_username || page.username,
          followers_count: page.ig_followers || page.instagram_followers || 0,
          profile_picture_url: page.ig_profile_picture_url || ''
        }
      : null);

  return {
    ...page,
    instagram: instagramFromVariants
  };
};

const hasValidInstagram = (page) => {
  if (!page?.instagram) return false;

  const igId = page.instagram.id || page.instagram.ig_account;
  const igUsername = page.instagram.username || page.instagram.ig_name;

  return Boolean(igId && igUsername);
};

const PageSelector = ({
  clientId,
  initialPages = [],
  connectionType = 'facebook',
  onComplete,
  onSkip
}) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialPages.length > 0) {
      const normalized = initialPages.map(normalizePage);
      const filtered =
        connectionType === 'instagram'
          ? normalized.filter(hasValidInstagram)
          : normalized;

      setPages(filtered);
      setLoading(false);

      if (filtered.length === 0) {
        setError(
          connectionType === 'instagram'
            ? 'No Instagram Business accounts linked to your Facebook pages were found.'
            : 'No Facebook pages found for this account.'
        );
      }

      return;
    }

    fetchPages();
  }, [clientId, connectionType, initialPages]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getFacebookPages(
        process.env.REACT_APP_API_FACEBOOK_OAUTH_GET_PAGES,
        clientId
      );

      if (response.success) {
        const normalizedPages = (response.pages || []).map(normalizePage);
        const filteredPages =
          connectionType === 'instagram'
            ? normalizedPages.filter(hasValidInstagram)
            : normalizedPages;

        setPages(filteredPages);

        if (filteredPages.length === 0) {
          setError(
            connectionType === 'instagram'
              ? 'No Instagram Business accounts linked to your Facebook pages were found.'
              : 'No Facebook pages found for this account.'
          );
        }
      } else {
        setError(response.error || 'Failed to fetch pages');
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('An error occurred while fetching pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPage = async (page) => {
    try {
      setSaving(true);
      setError(null);

      const response = await saveFacebookPage(
        process.env.REACT_APP_API_FACEBOOK_OAUTH_SAVE_PAGE,
        clientId,
        page,
        connectionType
      );

      if (response.success) {
        setSelectedPage(page);
        // Call onComplete after a short delay to show success state
        setTimeout(() => {
          onComplete(page);
        }, 1500);
      } else {
        setError(response.error || 'Failed to save page');
        setSaving(false);
      }
    } catch (err) {
      console.error('Error saving page:', err);
      setError('An error occurred while saving the page');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle>Select Facebook Page</CardTitle>
            <CardDescription>
              {connectionType === 'instagram'
                ? 'Loading your Instagram Business accounts...'
                : 'Loading your Facebook pages...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {connectionType === 'instagram'
              ? 'Select Instagram Business Account'
              : 'Select Facebook Page or Instagram Account'}
          </CardTitle>
          <CardDescription>
            {connectionType === 'instagram'
              ? 'Choose which Instagram Business account you want to connect to this client'
              : 'Choose which page or Instagram account you want to connect to this client'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {pages.length === 0 && !error && (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No pages found</p>
            </div>
          )}

          <div className="space-y-3">
            {pages.map((page) => (
              <div
                key={page.id}
                className={`relative p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedPage?.id === page.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                } ${saving ? 'opacity-50 pointer-events-none' : ''}`}
                onClick={() => !saving && handleSelectPage(page)}>
                {/* Facebook Page */}
                <div className="flex items-start gap-4">
                  {connectionType === 'instagram' ? (
                    <div className="h-12 w-12 flex-shrink-0 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                      <Instagram className="h-6 w-6 text-white" />
                    </div>
                  ) : (
                    <div className="h-12 w-12 flex-shrink-0 rounded-full bg-blue-600 flex items-center justify-center">
                      <Facebook className="h-6 w-6 text-white" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-base truncate">
                        {connectionType === 'instagram'
                          ? `@${page.instagram?.username || page.name}`
                          : page.name}
                      </h3>
                      {page.category && (
                        <Badge variant="secondary" className="text-xs">
                          {page.category}
                        </Badge>
                      )}
                    </div>

                    {page.followers_count && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                        <Users className="h-4 w-4" />
                        <span>
                          {page.followers_count.toLocaleString()} followers
                        </span>
                      </div>
                    )}

                    {connectionType === 'instagram' && (
                      <p className="text-sm text-gray-500 mb-2">
                        Linked Facebook page: {page.name || 'Unknown page'}
                      </p>
                    )}

                    {/* Instagram Business Account (si existe) */}
                    {connectionType !== 'instagram' && page.instagram && (
                      <div className="mt-3 pl-4 border-l-2 border-pink-200">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 flex-shrink-0 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                            <Instagram className="h-3 w-3 text-white" />
                          </div>
                          <span className="text-sm font-medium">
                            @{page.instagram.username}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            Instagram Business
                          </Badge>
                        </div>
                        {page.instagram.followers_count && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1 ml-8">
                            <Users className="h-3 w-3" />
                            <span>
                              {page.instagram.followers_count.toLocaleString()}{' '}
                              followers
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedPage?.id === page.id && (
                    <div className="flex-shrink-0">
                      {saving ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onSkip}
              disabled={saving}>
              {pages.length > 0 ? 'Skip for now' : 'Close'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageSelector;
